import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Badge } from '../../components/common/Badge';
import { Select } from '../../components/common/Select';
import { Spinner } from '../../components/common/Spinner';
import { useAdminAgents } from '../../hooks/useAgent';
import { formatDate } from '../../utils/formatters';
import { Store, Eye } from 'lucide-react';
import type { AgentProfile } from '../../types';

const STATUS_VARIANT: Record<string, any> = { PENDING: 'warning', APPROVED: 'success', REJECTED: 'danger', DISABLED: 'danger' };

export const AdminAgents: React.FC = () => {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const { data, isLoading } = useAdminAgents({ page, limit: 20, status: status || undefined });

  const agents: AgentProfile[] = data?.data?.agents || [];
  const pagination = data?.data?.pagination;

  return (
    <AdminLayout>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Agent Management</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Review and manage agent applications</p>
        </div>
      </div>

      <Card className="mb-6">
        <div className="flex items-end gap-4">
          <div className="w-48">
            <Select label="Status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}
              options={[{ value: '', label: 'All Statuses' }, { value: 'PENDING', label: 'Pending' }, { value: 'APPROVED', label: 'Approved' }, { value: 'REJECTED', label: 'Rejected' }, { value: 'DISABLED', label: 'Disabled' }]} />
          </div>
        </div>
      </Card>

      {isLoading ? (
        <div className="flex justify-center py-12"><Spinner size="lg" /></div>
      ) : agents.length === 0 ? (
        <Card><p className="text-center text-gray-400 dark:text-gray-500 py-12">No agent applications found.</p></Card>
      ) : (
        <>
          <div className="space-y-3">
            {agents.map((agent) => (
              <Card key={agent.id} className="hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="bg-indigo-100 dark:bg-indigo-900/50 p-2.5 rounded-xl">
                      <Store className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-semibold text-gray-900 dark:text-gray-100">
                          {agent.user?.firstName} {agent.user?.lastName}
                        </p>
                        <Badge variant={STATUS_VARIANT[agent.status]}>{agent.status}</Badge>
                        {agent.canPayoutLargeWins && <Badge variant="default">Super Agent</Badge>}
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{agent.user?.email}</p>
                      {agent.businessName && <p className="text-sm text-gray-600 dark:text-gray-300">{agent.businessName}</p>}
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{agent.storePrimaryActivity} · Applied {formatDate(agent.createdAt)}</p>
                    </div>
                  </div>
                  <Link to={`/admin/agents/${agent.id}`}>
                    <Button size="sm" variant="outline"><Eye className="w-4 h-4 mr-1" />Review</Button>
                  </Link>
                </div>
              </Card>
            ))}
          </div>
          {pagination && pagination.pages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-8">
              <Button variant="outline" disabled={page === 1} onClick={() => { setPage(page - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>Previous</Button>
              <span className="text-sm text-gray-500 dark:text-gray-400">Page {page} of {pagination.pages}</span>
              <Button variant="outline" disabled={page === pagination.pages} onClick={() => { setPage(page + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>Next</Button>
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
};
